<script setup>
/* eslint-disable vue/no-root-v-if */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import AipayOKSupportAPI from 'dashboard/api/aipayokSupport';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  contact: {
    type: Object,
    default: () => ({}),
  },
  conversation: {
    type: Object,
    default: () => ({}),
  },
});

const { t, te } = useI18n();
const businessContext = ref(null);
const isLoading = ref(false);
const hasError = ref(false);

const conversationAttributes = computed(
  () => props.conversation.custom_attributes || {}
);

const isVerified = computed(
  () => props.conversation.meta?.hmac_verified === true
);

const isAipayOKConversation = computed(
  () =>
    conversationAttributes.value.source_site === 'AipayOK' ||
    (isVerified.value && /^\d+$/.test(String(props.contact.identifier || '')))
);

const liveUser = computed(() => businessContext.value?.user || {});
const summary = computed(() => businessContext.value?.summary || {});
const orders = computed(() => businessContext.value?.orders || []);
const latestOrder = computed(() => orders.value[0] || {});
const subscriptions = computed(
  () => businessContext.value?.subscriptions || []
);
const afterSalesOrders = computed(() =>
  orders.value.filter(order => {
    const status = String(order.afterSalesStatus || '').toLowerCase();
    return status && !['normal', 'none'].includes(status);
  })
);
const links = computed(() => businessContext.value?.links || {});

const translateValue = (group, value) => {
  if (!value) return '';

  const key = `CONVERSATION_SIDEBAR.AIPAYOK.${group}.${String(value).toUpperCase()}`;
  // Status keys come from the trusted AipayOK API enum values.
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return te(key) ? t(key) : t('CONVERSATION_SIDEBAR.AIPAYOK.UNKNOWN_STATUS');
};

const accountStatus = computed(() =>
  translateValue('ACCOUNT_STATUS', liveUser.value.status)
);

const afterSalesStatus = computed(() =>
  translateValue('AFTER_SALES_STATUS', latestOrder.value.afterSalesStatus)
);

const formatDate = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

const details = computed(() =>
  [
    {
      key: 'business_user_id',
      label: t('CONVERSATION_SIDEBAR.AIPAYOK.BUSINESS_USER_ID'),
      value: liveUser.value.id,
      copy: true,
    },
    {
      key: 'email',
      label: t('CONVERSATION_SIDEBAR.AIPAYOK.EMAIL'),
      value: liveUser.value.email,
      copy: true,
    },
    {
      key: 'account_status',
      label: t('CONVERSATION_SIDEBAR.AIPAYOK.ACCOUNT_STATUS_LABEL'),
      value: accountStatus.value,
    },
    {
      key: 'balance',
      label: t('CONVERSATION_SIDEBAR.AIPAYOK.BALANCE'),
      value:
        liveUser.value.balance === undefined
          ? undefined
          : `¥${Number(liveUser.value.balance).toFixed(2)}`,
    },
    {
      key: 'invite_code',
      label: t('CONVERSATION_SIDEBAR.AIPAYOK.INVITE_CODE'),
      value: liveUser.value.inviteCode,
      copy: true,
    },
    {
      key: 'latest_order_no',
      label: t('CONVERSATION_SIDEBAR.AIPAYOK.LATEST_ORDER_NO'),
      value: latestOrder.value.orderNo,
      copy: true,
    },
    {
      key: 'latest_product_name',
      label: t('CONVERSATION_SIDEBAR.AIPAYOK.LATEST_PRODUCT'),
      value: latestOrder.value.planName,
    },
    {
      key: 'latest_order_status',
      label: t('CONVERSATION_SIDEBAR.AIPAYOK.LATEST_ORDER_STATUS'),
      value: translateValue('ORDER_STATUS', latestOrder.value.status),
    },
    {
      key: 'latest_after_sales_status',
      label: t('CONVERSATION_SIDEBAR.AIPAYOK.AFTER_SALES_STATUS_LABEL'),
      value: afterSalesStatus.value,
    },
    {
      key: 'active_subscription_count',
      label: t('CONVERSATION_SIDEBAR.AIPAYOK.ACTIVE_SUBSCRIPTIONS'),
      value: summary.value.activeSubscriptionCount,
    },
    {
      key: 'order_count',
      label: t('CONVERSATION_SIDEBAR.AIPAYOK.ORDER_COUNT'),
      value: summary.value.orderCount,
    },
    {
      key: 'open_after_sales_count',
      label: t('CONVERSATION_SIDEBAR.AIPAYOK.OPEN_AFTER_SALES'),
      value: summary.value.openAfterSalesCount,
    },
    {
      key: 'support_scene',
      label: t('CONVERSATION_SIDEBAR.AIPAYOK.SUPPORT_SCENE_LABEL'),
      value: translateValue(
        'SUPPORT_SCENE',
        conversationAttributes.value.support_scene
      ),
    },
    {
      key: 'context_order_no',
      label: t('CONVERSATION_SIDEBAR.AIPAYOK.CONTEXT_ORDER_NO'),
      value: conversationAttributes.value.context_order_no,
      copy: true,
    },
    {
      key: 'entry_page',
      label: t('CONVERSATION_SIDEBAR.AIPAYOK.ENTRY_PAGE'),
      value: conversationAttributes.value.entry_page,
    },
    {
      key: 'registered_at',
      label: t('CONVERSATION_SIDEBAR.AIPAYOK.REGISTERED_AT'),
      value: formatDate(liveUser.value.createdAt),
    },
    {
      key: 'last_login_at',
      label: t('CONVERSATION_SIDEBAR.AIPAYOK.LAST_LOGIN_AT'),
      value: formatDate(liveUser.value.lastLoginAt),
    },
  ].filter(({ value }) => value !== '' && value !== null && value !== undefined)
);

const fetchBusinessContext = async () => {
  businessContext.value = null;
  hasError.value = false;
  if (!isVerified.value || !props.conversation.id) return;

  isLoading.value = true;
  try {
    const { data } = await AipayOKSupportAPI.show(props.conversation.id);
    businessContext.value = data;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const copyValue = async value => {
  await copyTextToClipboard(String(value));
  useAlert(t('CONVERSATION_SIDEBAR.AIPAYOK.COPY_SUCCESS'));
};

watch(
  () => [
    props.conversation.id,
    props.conversation.meta?.hmac_verified,
    props.contact.identifier,
  ],
  fetchBusinessContext,
  { immediate: true }
);
</script>

<template>
  <section
    v-if="isAipayOKConversation"
    class="mx-2 mb-3 overflow-hidden rounded-lg border border-n-weak bg-n-surface-1"
  >
    <div
      class="flex items-center justify-between border-b border-n-weak px-3 py-2"
    >
      <div class="flex items-center gap-2">
        <span class="i-lucide-badge-info size-4 text-n-brand" />
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.TITLE') }}
        </h3>
      </div>
      <span
        class="rounded-full px-2 py-0.5 text-xs font-medium"
        :class="
          isVerified
            ? 'bg-n-teal-3 text-n-teal-11'
            : 'bg-n-amber-3 text-n-amber-11'
        "
      >
        {{
          isVerified
            ? $t('CONVERSATION_SIDEBAR.AIPAYOK.VERIFIED')
            : $t('CONVERSATION_SIDEBAR.AIPAYOK.UNVERIFIED')
        }}
      </span>
      <a
        v-if="links.user"
        :href="links.user"
        target="_blank"
        rel="noopener noreferrer"
        class="i-lucide-external-link size-4 text-n-slate-10 hover:text-n-brand"
        :aria-label="$t('CONVERSATION_SIDEBAR.AIPAYOK.OPEN_USER')"
      />
    </div>

    <dl class="divide-y divide-n-weak/60">
      <div
        v-for="item in details"
        :key="item.key"
        class="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] items-center gap-2 px-3 py-2 text-xs"
      >
        <dt class="text-n-slate-10">{{ item.label }}</dt>
        <dd
          class="flex min-w-0 items-center justify-end gap-1 text-right text-n-slate-12"
        >
          <span class="truncate" :title="String(item.value)">{{
            item.value
          }}</span>
          <button
            v-if="item.copy"
            type="button"
            class="i-lucide-copy size-3.5 shrink-0 text-n-slate-10 hover:text-n-slate-12"
            :aria-label="$t('CONVERSATION_SIDEBAR.AIPAYOK.COPY')"
            @click="copyValue(item.value)"
          />
        </dd>
      </div>
    </dl>

    <div
      v-if="isLoading"
      class="flex justify-center border-t border-n-weak py-4"
    >
      <Spinner />
    </div>
    <div
      v-else-if="hasError"
      class="flex items-center justify-between gap-2 border-t border-n-weak px-3 py-3"
    >
      <p class="text-xs text-n-ruby-10">
        {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.LOAD_ERROR') }}
      </p>
      <button
        type="button"
        class="text-xs font-medium text-n-brand hover:underline"
        @click="fetchBusinessContext"
      >
        {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.RETRY') }}
      </button>
    </div>

    <div v-if="orders.length" class="border-t border-n-weak px-3 py-3">
      <h4 class="mb-2 text-xs font-medium text-n-slate-12">
        {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.RECENT_ORDERS') }}
      </h4>
      <div class="flex flex-col gap-2">
        <div
          v-for="order in orders"
          :key="order.orderNo"
          class="rounded-lg bg-n-alpha-2 p-2.5 text-xs"
          :class="{
            'ring-1 ring-n-brand':
              order.orderNo === conversationAttributes.context_order_no,
          }"
        >
          <div class="flex items-start justify-between gap-2">
            <div class="flex min-w-0 items-center gap-1.5">
              <button
                type="button"
                class="min-w-0 truncate font-medium text-n-brand hover:underline"
                :title="order.orderNo"
                @click="copyValue(order.orderNo)"
              >
                {{ order.orderNo }}
              </button>
              <a
                v-if="links.orders?.[order.orderNo]"
                :href="links.orders[order.orderNo]"
                target="_blank"
                rel="noopener noreferrer"
                class="i-lucide-external-link size-3.5 shrink-0 text-n-slate-9 hover:text-n-brand"
                :aria-label="$t('CONVERSATION_SIDEBAR.AIPAYOK.OPEN_ORDER')"
              />
            </div>
            <span class="shrink-0 text-n-slate-10">
              {{ translateValue('ORDER_STATUS', order.status) }}
            </span>
          </div>
          <p class="mt-1 truncate text-n-slate-12">{{ order.planName }}</p>
          <div class="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-n-slate-10">
            <span>
              {{
                $t('CONVERSATION_SIDEBAR.AIPAYOK.AMOUNT', {
                  amount: Number(order.amount || 0).toFixed(2),
                })
              }}
            </span>
            <span>{{ formatDate(order.createdAt) }}</span>
            <span v-if="order.deliveryStatus">
              {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.DELIVERY')
              }}{{ $t('CONVERSATION_SIDEBAR.AIPAYOK.LABEL_SEPARATOR') }}
              {{ translateValue('DELIVERY_STATUS', order.deliveryStatus) }}
            </span>
            <span v-if="order.refundStatus">
              {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.REFUND')
              }}{{ $t('CONVERSATION_SIDEBAR.AIPAYOK.LABEL_SEPARATOR') }}
              {{ translateValue('REFUND_STATUS', order.refundStatus) }}
            </span>
            <span v-if="order.afterSalesStatus">
              {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.AFTER_SALES')
              }}{{ $t('CONVERSATION_SIDEBAR.AIPAYOK.LABEL_SEPARATOR') }}
              {{ translateValue('AFTER_SALES_STATUS', order.afterSalesStatus) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="afterSalesOrders.length"
      class="border-t border-n-weak px-3 py-3"
    >
      <h4 class="mb-2 text-xs font-medium text-n-slate-12">
        {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.AFTER_SALES_RECORDS') }}
      </h4>
      <div class="flex flex-col gap-1.5 text-xs">
        <div
          v-for="order in afterSalesOrders"
          :key="`after-sales-${order.orderNo}`"
          class="flex items-center justify-between gap-2 rounded-lg bg-n-alpha-2 px-2.5 py-2"
        >
          <div class="min-w-0">
            <div class="flex min-w-0 items-center gap-1.5">
              <button
                type="button"
                class="truncate font-medium text-n-brand hover:underline"
                :title="order.orderNo"
                @click="copyValue(order.orderNo)"
              >
                {{ order.orderNo }}
              </button>
              <a
                v-if="links.orders?.[order.orderNo]"
                :href="links.orders[order.orderNo]"
                target="_blank"
                rel="noopener noreferrer"
                class="i-lucide-external-link size-3.5 shrink-0 text-n-slate-9 hover:text-n-brand"
                :aria-label="$t('CONVERSATION_SIDEBAR.AIPAYOK.OPEN_ORDER')"
              />
            </div>
            <p class="truncate text-n-slate-10">{{ order.planName }}</p>
          </div>
          <span class="shrink-0 text-n-slate-10">
            {{ translateValue('AFTER_SALES_STATUS', order.afterSalesStatus) }}
          </span>
        </div>
      </div>
    </div>

    <div v-if="subscriptions.length" class="border-t border-n-weak px-3 py-3">
      <h4 class="mb-2 text-xs font-medium text-n-slate-12">
        {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.SUBSCRIPTIONS') }}
      </h4>
      <div class="flex flex-col gap-1.5 text-xs">
        <div
          v-for="subscription in subscriptions"
          :key="subscription.id"
          class="flex items-center justify-between gap-2 rounded-lg bg-n-alpha-2 px-2.5 py-2"
        >
          <span class="min-w-0 truncate text-n-slate-12">
            {{ subscription.planName }}
          </span>
          <span class="shrink-0 text-n-slate-10">
            {{ translateValue('SUBSCRIPTION_STATUS', subscription.status) }}
            <template v-if="subscription.expireDate">
              {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.DETAIL_SEPARATOR') }}
              {{ formatDate(subscription.expireDate) }}
            </template>
          </span>
        </div>
      </div>
    </div>
  </section>
</template>
